import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, TextField, Typography, Alert, useTheme } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import FormContainer from '../../../core/components/layout/FormContainer';
import ActionButton from '../../../core/components/ui/ActionButton';
import { postStagingPassword } from '../api/staging.api';
import { StagingPasswordRequest, StagingPasswordErrorResponse } from '../api/staging.api';

const STAGING_STORAGE_KEYS = {
  ATTEMPTS: 'staging_password_attempts',
  LOCKOUT_UNTIL: 'staging_password_lockout_until',
};

const MAX_ATTEMPTS = 5;
const LOCKOUT_DURATION_MS = 15 * 60 * 1000; // 15 minutes

interface StagingPasswordProps {
  onValidationSuccess?: () => void;
}

const StagingPassword: React.FC<StagingPasswordProps> = ({ onValidationSuccess }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [attempts, setAttempts] = useState<number>(0);
  const [lockoutUntil, setLockoutUntil] = useState<number | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);

  // Load attempts and lockout from localStorage on mount
  useEffect(() => {
    const storedAttempts = localStorage.getItem(STAGING_STORAGE_KEYS.ATTEMPTS);
    const storedLockout = localStorage.getItem(STAGING_STORAGE_KEYS.LOCKOUT_UNTIL);

    if (storedAttempts) {
      setAttempts(parseInt(storedAttempts, 10));
    }

    if (storedLockout) {
      const lockoutTime = parseInt(storedLockout, 10);
      const now = Date.now();

      if (lockoutTime > now) {
        setLockoutUntil(lockoutTime);
      } else {
        // Lockout expired, clear it
        localStorage.removeItem(STAGING_STORAGE_KEYS.LOCKOUT_UNTIL);
        localStorage.removeItem(STAGING_STORAGE_KEYS.ATTEMPTS);
        setAttempts(0);
        setLockoutUntil(null);
      }
    }
  }, [navigate]);

  // Update time remaining for lockout
  useEffect(() => {
    if (!lockoutUntil) {
      setTimeRemaining(0);
      return;
    }

    const updateTimeRemaining = () => {
      const now = Date.now();
      const remaining = Math.max(0, lockoutUntil - now);

      if (remaining === 0) {
        setLockoutUntil(null);
        localStorage.removeItem(STAGING_STORAGE_KEYS.LOCKOUT_UNTIL);
        localStorage.removeItem(STAGING_STORAGE_KEYS.ATTEMPTS);
        setAttempts(0);
      } else {
        setTimeRemaining(remaining);
      }
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [lockoutUntil]);

  const formatTimeRemaining = useCallback((ms: number): string => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  const isLockedOut = useMemo(() => {
    return lockoutUntil !== null && lockoutUntil > Date.now();
  }, [lockoutUntil]);

  const handlePasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    setError('');
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (isLockedOut) {
        return;
      }

      if (!password.trim()) {
        setError('Please enter the staging password');
        return;
      }

      setIsSubmitting(true);
      setError('');

      try {
        const payload: StagingPasswordRequest = {
          stagingPassword: password,
        };

        const response = await postStagingPassword(payload);
        const responseData = response.data;

        // Success
        if (responseData.statusCode === 200 && responseData.isValid) {
          // Clear attempts and lockout
          localStorage.removeItem(STAGING_STORAGE_KEYS.ATTEMPTS);
          localStorage.removeItem(STAGING_STORAGE_KEYS.LOCKOUT_UNTIL);
          setAttempts(0);
          setLockoutUntil(null);

          // Notify parent component of successful validation (in-memory only)
          onValidationSuccess?.();

          // Navigate to auth page (no storage - password required every time)
          navigate('/auth', { replace: true });
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (err: unknown) {
        const axiosError = err as { response?: { data?: StagingPasswordErrorResponse } };
        const errorResponse = axiosError.response?.data;

        if (errorResponse?.error) {
          const { statusCode, message } = errorResponse.error;

          if (statusCode === 429) {
            // Too many attempts - set lockout
            const lockoutTime = Date.now() + LOCKOUT_DURATION_MS;
            setLockoutUntil(lockoutTime);
            localStorage.setItem(STAGING_STORAGE_KEYS.LOCKOUT_UNTIL, lockoutTime.toString());
            setError('Too many failed attempts. Please try again in 15 minutes.');
          } else if (statusCode === 403) {
            // Invalid password - increment attempts
            const newAttempts = attempts + 1;
            setAttempts(newAttempts);
            localStorage.setItem(STAGING_STORAGE_KEYS.ATTEMPTS, newAttempts.toString());

            if (newAttempts >= MAX_ATTEMPTS) {
              // Lockout after max attempts
              const lockoutTime = Date.now() + LOCKOUT_DURATION_MS;
              setLockoutUntil(lockoutTime);
              localStorage.setItem(STAGING_STORAGE_KEYS.LOCKOUT_UNTIL, lockoutTime.toString());
              setError('Too many failed attempts. Please try again in 15 minutes.');
            } else {
              setError(
                `Invalid password. ${MAX_ATTEMPTS - newAttempts} attempt${
                  MAX_ATTEMPTS - newAttempts === 1 ? '' : 's'
                } remaining.`
              );
            }
          } else {
            setError(message || 'An error occurred. Please try again.');
          }
        } else {
          setError('An error occurred. Please try again.');
        }

        setPassword('');
      } finally {
        setIsSubmitting(false);
      }
    },
    [password, attempts, isLockedOut, navigate, onValidationSuccess]
  );

  return (
    <FormContainer
      snackBarOpts={{
        useSnackBar: false,
        showSnackbar: false,
        message: '',
        duration: 6000,
        error: false,
        warning: false,
        sx: {},
      }}
      secondOptionActionText=''
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          maxWidth: 400,
          mx: 'auto',
          mt: 4,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <LockIcon
            sx={{
              fontSize: 48,
              color: theme.palette.primary.main,
              mr: 2,
            }}
          />
          <Typography variant='h4' component='h1' fontWeight={600}>
            Staging Access
          </Typography>
        </Box>

        <Typography variant='body1' color='text.secondary' sx={{ mb: 4, textAlign: 'center' }}>
          Please enter the staging password to continue
        </Typography>

        <Box component='form' onSubmit={handleSubmit} sx={{ width: '100%' }}>
          <TextField
            fullWidth
            type='password'
            label='Staging Password'
            value={password}
            onChange={handlePasswordChange}
            disabled={isSubmitting || isLockedOut}
            error={!!error}
            autoFocus
            sx={{ mb: 2 }}
            slotProps={{
              htmlInput: {
                autoComplete: 'off',
              },
            }}
          />

          {error && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isLockedOut && timeRemaining > 0 && (
            <Alert severity='warning' sx={{ mb: 2 }}>
              Account locked. Please try again in {formatTimeRemaining(timeRemaining)}.
            </Alert>
          )}

          <ActionButton
            type='submit'
            text='Continue'
            loading={isSubmitting}
            disabled={isSubmitting || isLockedOut || !password.trim()}
            testId='staging-password-submit'
          />
        </Box>
      </Box>
    </FormContainer>
  );
};

export default StagingPassword;
